import {
  parseFleetMapHostMessage,
  type FleetMapHostMessage,
} from '@bonbon/types';

/**
 * Everything this app trusts comes from the one page that embeds it. A message
 * counts only when it was sent by the parent window itself *and* from an
 * origin on the build-time allowlist; anything else (another frame, another
 * tab with a reference to this window, a stray visit) is dropped unread.
 */
export function isTrustedHostEvent(
  event: Pick<MessageEvent, 'origin' | 'source'>,
  hostWindow: Window,
  allowedOrigins: readonly string[],
): boolean {
  return event.source === hostWindow && allowedOrigins.includes(event.origin);
}

export interface TrustedHostMessage {
  /** The verified origin: the only `targetOrigin` this app replies to. */
  origin: string;
  message: FleetMapHostMessage;
}

/** The host's message in a trusted event, or null for anything else. */
export function readHostEvent(
  event: Pick<MessageEvent, 'origin' | 'source' | 'data'>,
  hostWindow: Window,
  allowedOrigins: readonly string[],
): TrustedHostMessage | null {
  if (!isTrustedHostEvent(event, hostWindow, allowedOrigins)) return null;
  const message = parseFleetMapHostMessage(event.data);
  return message === null ? null : { origin: event.origin, message };
}

/**
 * The origins to announce `ready` to before the host has said anything. A
 * `postMessage` is delivered only when the receiver's origin equals the
 * target, so naming the origins we would accept reaches the real host and no
 * one else. Where the browser tells us the parent's origin
 * (`location.ancestorOrigins`: Chromium, Safari), only that one is tried and
 * only if allowed; Firefox has no such list, so every allowed origin is.
 * Empty when the page is not framed (a stray visit stays idle).
 */
export function readyTargetOrigins(
  ancestorOrigins: readonly string[] | undefined,
  allowedOrigins: readonly string[],
  isFramed: boolean,
): string[] {
  if (!isFramed) return [];
  const parent = ancestorOrigins?.[0];
  if (parent === undefined) return [...allowedOrigins];
  return allowedOrigins.includes(parent) ? [parent] : [];
}
